"use strict";

exports.__esModule = true;
exports.BAND_TILE_TYPES = void 0;
exports.HeaderBand = HeaderBand;
exports.splitHeaderBand = splitHeaderBand;
var _react = _interopRequireWildcard(require("react"));
var _styledComponents = _interopRequireDefault(require("styled-components"));
var _Theme = require("../../Theme");
var _TimeContext = require("../../Time/TimeContext");
var _Timer = require("../../Time/Timer");
var _FormattedPlantDate = require("../../Time/FormattedPlantDate");
var _Utils = require("../../../Utils");
var _TileContext = require("../common/TileContext");
var _DeviceState = require("./DeviceState");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { "default": e }; }
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function _interopRequireWildcard(e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, "default": e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (var _t in e) "default" !== _t && {}.hasOwnProperty.call(e, _t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, _t)) && (i.get || i.set) ? o(f, _t, i) : f[_t] = e[_t]); return f; })(e, t); }
// The header from the next-gen operator panel: a solid state-coloured hero on
// the left naming the machine and its current state, the KPIs as a row of
// equal white tiles beside it, and the action buttons as a full-width row
// underneath. Built from the value tiles a panel places at the top of its
// grid and the buttons directly beneath, whatever sizes they were given.
var GAP = 10;
var CELL_GUTTER = 4;
var HERO_MIN_WIDTH = 240;
var HERO_MAX_WIDTH = 440;
var HERO_SHARE = 0.3;
var TILE_MIN_WIDTH = 96;
var ACTION_MIN_HEIGHT = 56;
var ACTION_MAX_HEIGHT = 60;
// The band may grow down into empty space under it by this much, so the hero
// gets its height back when a button row sits beneath the tiles.
var MAX_EXTEND = 120;
var NEUTRAL_STATE = "#D5DDE2";
var STATE_TIMELINE = "time-series-classification";
// Header tiles start within this share of the grid's height, and the header
// as a whole may not be taller than this share of it.
var HEADER_ZONE_SHARE = 0.4;
var HEADER_MAX_SHARE = 0.45;
var BAND_TILE_TYPES = exports.BAND_TILE_TYPES = ["DeviceProperty", "TimelineValue", "AdvancedTimelineValue", "MetricValue", "MetricValueAggregate", "PerformanceParameterValue", "Clock"];
var ACTION_TYPES = ["ActionButton"];

// The hero shows the machine name and its state, so those two tiles fold
// into it rather than repeating in the row.
var isIdentity = function isIdentity(_ref) {
  var child = _ref.child;
  return child.type === "DeviceProperty" || child.type === "TimelineValue" && child.props && child.props.timelineName === STATE_TIMELINE;
};
var union = function union(items) {
  var left = Math.min.apply(Math, items.map(function (item) {
    return item.cell.x;
  }));
  var top = Math.min.apply(Math, items.map(function (item) {
    return item.cell.y;
  }));
  var right = Math.max.apply(Math, items.map(function (item) {
    return item.cell.x + item.cell.width;
  }));
  var bottom = Math.max.apply(Math, items.map(function (item) {
    return item.cell.y + item.cell.height;
  }));
  return {
    x: left,
    y: top,
    width: right - left,
    height: bottom - top
  };
};

/**
 * Find the header of a device's grid: the value tiles that sit at the top
 * with nothing but other header tiles above them in their own columns, and
 * the row of action buttons directly beneath them if there is one. Returns
 * `{ band, actions, rest, rect }` or null when the grid has no such header.
 */
function splitHeaderBand(children, getCell) {
  var items = children.map(function (child, index) {
    return {
      child: child,
      index: index,
      cell: getCell(child)
    };
  });
  var tiles = items.filter(function (item) {
    return BAND_TILE_TYPES.includes(item.child.type);
  });
  var others = items.filter(function (item) {
    return !BAND_TILE_TYPES.includes(item.child.type);
  });
  if (others.length === 0 || tiles.length < 3) {
    return null;
  }
  var gridTop = Math.min.apply(Math, items.map(function (item) {
    return item.cell.y;
  }));
  var gridBottom = Math.max.apply(Math, items.map(function (item) {
    return item.cell.y + item.cell.height;
  }));
  var gridHeight = gridBottom - gridTop;

  // A tile is part of the header when it starts in the top part of the grid
  // and no other kind of widget sits above it in the columns it spans.
  var overlapsX = function overlapsX(a, b) {
    return a.cell.x < b.cell.x + b.cell.width - 1 && a.cell.x + a.cell.width - 1 > b.cell.x;
  };
  var isHeaderTile = function isHeaderTile(tile) {
    return tile.cell.y <= gridTop + gridHeight * HEADER_ZONE_SHARE && others.every(function (other) {
      return other.cell.y >= tile.cell.y + tile.cell.height - 1 || !overlapsX(other, tile);
    });
  };
  var band = tiles.filter(isHeaderTile);
  var rect = band.length ? union(band) : null;

  // A tall or narrow block of tiles is a sidebar, not a header: fall back to
  // the tiles above everything else.
  var isHeaderShape = function isHeaderShape(r) {
    return r.height <= gridHeight * HEADER_MAX_SHARE && r.height <= r.width * 0.5;
  };
  if (!rect || !isHeaderShape(rect)) {
    var firstOtherTop = Math.min.apply(Math, others.map(function (item) {
      return item.cell.y;
    }));
    band = tiles.filter(function (item) {
      return item.cell.y + item.cell.height <= firstOtherTop + 1;
    });
    rect = band.length ? union(band) : null;
  }
  // The header needs to be at the top, wide enough for the hero and a couple
  // of tiles, and shallow; otherwise the grid keeps its own layout.
  if (!rect || band.length < 3 || rect.y > gridTop + 1 || !isHeaderShape(rect) || rect.width < HERO_MIN_WIDTH + GAP + 2 * TILE_MIN_WIDTH) {
    return null;
  }

  // Buttons that sit right under the band, above any other content.
  var buttons = others.filter(function (item) {
    return ACTION_TYPES.includes(item.child.type) && item.cell.y >= rect.y + rect.height - 1;
  });
  var nonButtons = others.filter(function (item) {
    return !ACTION_TYPES.includes(item.child.type);
  });
  var nextTop = nonButtons.length ? Math.min.apply(Math, nonButtons.map(function (item) {
    return item.cell.y;
  })) : Infinity;
  var actions = buttons.filter(function (item) {
    return item.cell.y + item.cell.height <= nextTop + 1;
  });
  var taken = new Set([].concat(band, actions));
  var rest = items.filter(function (item) {
    return !taken.has(item);
  });
  var bandRectAll = union([].concat(band, actions));

  // Empty space between the band and the next content below it is the
  // band's to use.
  var below = rest.filter(function (item) {
    return item.cell.y >= bandRectAll.y + bandRectAll.height - 1 && item.cell.x < bandRectAll.x + bandRectAll.width && item.cell.x + item.cell.width > bandRectAll.x;
  });
  if (below.length) {
    var _nextTop = Math.min.apply(Math, below.map(function (item) {
      return item.cell.y;
    }));
    var extra = _nextTop - (bandRectAll.y + bandRectAll.height);
    if (extra > 0) {
      bandRectAll = Object.assign({}, bandRectAll, {
        height: bandRectAll.height + Math.min(extra, MAX_EXTEND)
      });
    }
  }

  // Likewise the empty space above it, up to the top of the grid.
  var above = rest.filter(function (item) {
    return item.cell.y + item.cell.height <= bandRectAll.y + 1 && item.cell.x < bandRectAll.x + bandRectAll.width && item.cell.x + item.cell.width > bandRectAll.x;
  });
  var ceiling = above.length ? Math.max.apply(Math, above.map(function (item) {
    return item.cell.y + item.cell.height;
  })) : 0;
  var headroom = Math.min(bandRectAll.y - ceiling, MAX_EXTEND);
  if (headroom > 0) {
    bandRectAll = Object.assign({}, bandRectAll, {
      y: bandRectAll.y - headroom,
      height: bandRectAll.height + headroom
    });
  }

  // Never draw the band over another widget.
  var intersects = function intersects(a, r) {
    return a.cell.x < r.x + r.width && a.cell.x + a.cell.width > r.x && a.cell.y < r.y + r.height && a.cell.y + a.cell.height > r.y;
  };
  if (rest.some(function (item) {
    return intersects(item, bandRectAll);
  })) {
    return null;
  }
  return {
    band: band,
    actions: actions,
    actionsRect: actions.length ? union(actions) : null,
    rest: rest,
    rect: bandRectAll
  };
}
var byPosition = function byPosition(a, b) {
  return a.cell.y - b.cell.y || a.cell.x - b.cell.x || a.index - b.index;
};
var Band = _styledComponents["default"].div.withConfig({
  displayName: "HeaderBand__Band",
  componentId: "sc-126ovzy-0"
})(["position:absolute;box-sizing:border-box;display:flex;flex-direction:column;gap:", "px;color:#1b2227;"], GAP);
var TopRow = _styledComponents["default"].div.withConfig({
  displayName: "HeaderBand__TopRow",
  componentId: "sc-126ovzy-1"
})(["flex:none;display:flex;gap:", "px;min-width:0;"], GAP);
var Hero = _styledComponents["default"].div.withConfig({
  displayName: "HeaderBand__Hero",
  componentId: "sc-126ovzy-2"
})(["flex:none;box-sizing:border-box;display:flex;flex-direction:column;justify-content:center;gap:3px;min-width:0;border-radius:12px;box-shadow:", ";overflow:hidden;transition:background 0.4s ease;"], (0, _Theme.val)("CardShadow"));

// The machine name leads; the state under it is a little smaller and lighter
// because the card's colour already says what the state is.
var HeroName = _styledComponents["default"].div.withConfig({
  displayName: "HeaderBand__HeroName",
  componentId: "sc-126ovzy-3"
})(["font-family:", ";font-weight:800;letter-spacing:-0.02em;line-height:1.1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;"], _TileContext.FLAT_FONT_HEADING);
var HeroLabel = _styledComponents["default"].div.withConfig({
  displayName: "HeaderBand__HeroLabel",
  componentId: "sc-126ovzy-4"
})(["margin-top:6px;font-size:10px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;line-height:1.2;opacity:0.7;"]);
var HeroState = _styledComponents["default"].div.withConfig({
  displayName: "HeaderBand__HeroState",
  componentId: "sc-126ovzy-5"
})(["font-family:", ";font-weight:600;letter-spacing:-0.01em;line-height:1.15;opacity:0.92;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;"], _TileContext.FLAT_FONT_HEADING);
var HeroSince = _styledComponents["default"].div.withConfig({
  displayName: "HeaderBand__HeroSince",
  componentId: "sc-126ovzy-6"
})(["margin-top:2px;font-family:", ";font-size:11.5px;line-height:1.2;opacity:0.8;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;"], _TileContext.FLAT_FONT_MONO);
var Tiles = _styledComponents["default"].div.withConfig({
  displayName: "HeaderBand__Tiles",
  componentId: "sc-126ovzy-7"
})(["flex:1 1 auto;min-width:0;display:flex;flex-wrap:wrap;align-content:flex-start;gap:", "px;"], GAP);
var Actions = _styledComponents["default"].div.withConfig({
  displayName: "HeaderBand__Actions",
  componentId: "sc-126ovzy-8"
})(["flex:none;display:flex;gap:", "px;"], GAP);
function fitTiles(_ref2) {
  var count = _ref2.count,
    width = _ref2.width,
    height = _ref2.height;
  if (count === 0) {
    return {
      tileWidth: 0,
      tileHeight: 0
    };
  }
  var perRowMax = Math.max(1, Math.floor((width + GAP) / (TILE_MIN_WIDTH + GAP)));
  var rows = Math.ceil(count / Math.min(perRowMax, count));
  var perRow = Math.ceil(count / rows);
  return {
    tileWidth: Math.max(TILE_MIN_WIDTH, Math.floor((width - GAP * (perRow - 1)) / perRow)),
    tileHeight: Math.max(24, Math.floor((height - GAP * (rows - 1)) / rows))
  };
}

// What fits in the hero at a given height. Lines are tried richest first:
// name, caption, state and "since"; then without the caption; then without
// "since"; and when there is room for nothing else, the name alone.
var HERO_LABEL_HEIGHT = 18;
var HERO_SINCE_HEIGHT = 16;
var HERO_LINE_GAP = 3;
function heroLayout(height) {
  var pad = height >= 120 ? 14 : 8;
  var available = height - 2 * pad;
  var nameSize = function nameSize(share) {
    return Math.round(Math.max(14, Math.min(30, available * share)));
  };
  var attempt = function attempt(share, showLabel, showSince, showState) {
    var nameFontSize = nameSize(share);
    var stateFontSize = showState ? Math.round(nameFontSize * 0.78) : 0;
    var lines = 1 + (showLabel ? 1 : 0) + (showState ? 1 : 0) + (showSince ? 1 : 0);
    var needed = nameFontSize * 1.1 + (showLabel ? HERO_LABEL_HEIGHT : 0) + (showState ? stateFontSize * 1.15 : 0) + (showSince ? HERO_SINCE_HEIGHT : 0) + HERO_LINE_GAP * (lines - 1);
    return needed <= available ? {
      showLabel: showLabel,
      showSince: showSince,
      showState: showState,
      pad: pad,
      nameFontSize: nameFontSize,
      stateFontSize: stateFontSize
    } : null;
  };
  return attempt(0.25, true, true, true) || attempt(0.3, false, true, true) || attempt(0.34, false, false, true) || attempt(0.6, false, false, false) || {
    showLabel: false,
    showSince: false,
    showState: false,
    pad: pad,
    nameFontSize: 14,
    stateFontSize: 0
  };
}
function HeroCard(_ref3) {
  var device = _ref3.device,
    width = _ref3.width,
    height = _ref3.height;
  var time = (0, _react.useContext)(_TimeContext.TimeContext) || {};
  var timeZone = time.timezone && time.timezone.name;
  var layout = heroLayout(height);
  return /*#__PURE__*/_react["default"].createElement(_DeviceState.DeviceStateSubscription, {
    deviceUuid: device.uuid
  }, function (_ref4) {
    var text = _ref4.text,
      color = _ref4.color,
      startMillis = _ref4.startMillis;
    return /*#__PURE__*/_react["default"].createElement(Hero, {
      "data-testid": "HeaderBand.Hero",
      style: {
        width: width,
        height: height,
        padding: layout.pad + "px " + (layout.pad + 6) + "px",
        background: color && color !== "transparent" ? color : NEUTRAL_STATE
      }
    }, /*#__PURE__*/_react["default"].createElement(HeroName, {
      title: device.name,
      style: {
        fontSize: layout.nameFontSize
      }
    }, device.name), layout.showLabel && /*#__PURE__*/_react["default"].createElement(HeroLabel, null, "Current state"), layout.showState && /*#__PURE__*/_react["default"].createElement(HeroState, {
      style: {
        fontSize: layout.stateFontSize
      }
    }, text || "—"), layout.showSince && startMillis > 0 && /*#__PURE__*/_react["default"].createElement(_Timer.Timer, {
      interval: 1000
    }, function (_ref5) {
      var now = _ref5.now;
      return /*#__PURE__*/_react["default"].createElement(HeroSince, null, "since", " ", (0, _FormattedPlantDate.formatDateString)(startMillis, Object.assign({
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit"
      }, timeZone ? {
        timeZone: timeZone
      } : {})), " · ", (0, _Utils.formatDuration)(Math.max(0, now - startMillis), true) || "0s");
    }));
  });
}
var renderAt = function renderAt(render, item, width, height, overrides) {
  if (overrides === void 0) {
    overrides = {};
  }
  return render(Object.assign({}, item.child, {
    props: Object.assign({}, item.child.props, {
      x: 0,
      y: 0,
      width: width,
      height: height
    }, overrides)
  }), item.index);
};

/**
 * Renders a grid's header: the hero beside the value tiles, with the action
 * buttons in a full-width row beneath both. Each tile and button still
 * renders through its own widget.
 */
function HeaderBand(_ref6) {
  var device = _ref6.device,
    band = _ref6.band,
    _ref6$actions = _ref6.actions,
    actions = _ref6$actions === void 0 ? [] : _ref6$actions,
    actionsRect = _ref6.actionsRect,
    _ref6$rest = _ref6.rest,
    rest = _ref6$rest === void 0 ? [] : _ref6$rest,
    rect = _ref6.rect,
    render = _ref6.render;
  var x = rect.x + CELL_GUTTER;
  var y = rect.y + CELL_GUTTER;
  var width = Math.max(0, rect.width - 2 * CELL_GUTTER);
  var height = Math.max(0, rect.height - 2 * CELL_GUTTER);
  var tiles = band.filter(function (item) {
    return !isIdentity(item);
  }).sort(byPosition);
  var buttons = [].concat(actions).sort(byPosition);

  // The buttons keep roughly the row height they were given; the hero and
  // the tiles share the rest.
  var actionsHeight = buttons.length ? Math.max(ACTION_MIN_HEIGHT, Math.min(ACTION_MAX_HEIGHT, actionsRect.height - 2 * CELL_GUTTER)) : 0;
  var topHeight = Math.max(24, height - (buttons.length ? actionsHeight + GAP : 0));
  var heroWidth = Math.round(Math.min(HERO_MAX_WIDTH, Math.max(HERO_MIN_WIDTH, width * HERO_SHARE)));
  var tilesWidth = Math.max(0, width - heroWidth - GAP);

  // The tiles stop above the first other widget that sits in their columns,
  // so a taller hero never pushes them over the content below.
  var tilesLeft = rect.x + heroWidth + GAP;
  var blockerTop = rest.filter(function (item) {
    return item.cell.y >= rect.y && item.cell.x < rect.x + rect.width && item.cell.x + item.cell.width > tilesLeft;
  }).reduce(function (top, item) {
    return Math.min(top, item.cell.y);
  }, Infinity);
  var tilesHeight = Math.max(24, Math.min(topHeight, blockerTop - rect.y - GAP));
  var _fitTiles = fitTiles({
      count: tiles.length,
      width: tilesWidth,
      height: tilesHeight
    }),
    tileWidth = _fitTiles.tileWidth,
    tileHeight = _fitTiles.tileHeight;
  var buttonWidth = buttons.length ? Math.floor((width - GAP * (buttons.length - 1)) / buttons.length) : 0;
  return /*#__PURE__*/_react["default"].createElement(Band, {
    "data-testid": "HeaderBand",
    style: {
      left: x,
      top: y,
      width: width,
      height: height
    }
  }, /*#__PURE__*/_react["default"].createElement(TopRow, {
    style: {
      height: topHeight
    }
  }, /*#__PURE__*/_react["default"].createElement(HeroCard, {
    device: device,
    width: heroWidth,
    height: topHeight
  }), /*#__PURE__*/_react["default"].createElement(Tiles, {
    style: {
      width: tilesWidth,
      height: tilesHeight
    }
  }, tiles.map(function (item) {
    return /*#__PURE__*/_react["default"].createElement("div", {
      key: item.index,
      style: {
        width: tileWidth,
        height: tileHeight
      }
    }, renderAt(render, item, tileWidth, tileHeight, {
      hideLabel: false,
      fontSize: undefined
    }));
  }))), buttons.length > 0 && /*#__PURE__*/_react["default"].createElement(Actions, {
    style: {
      height: actionsHeight
    }
  }, buttons.map(function (item) {
    return /*#__PURE__*/_react["default"].createElement("div", {
      key: item.index,
      style: {
        width: buttonWidth,
        height: actionsHeight
      }
    }, renderAt(render, item, buttonWidth, actionsHeight));
  })));
}